import { useState, useCallback, useEffect } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, Tabs, Alert,
  Tooltip, SimpleGrid,
} from '@mantine/core'
import {
  IconPlus, IconPencil, IconTrash, IconEye, IconEyeOff,
  IconBriefcase, IconAdjustmentsAlt, IconExternalLink,
  IconArrowUp, IconArrowDown, IconDeviceFloppy,
  IconAlertCircle, IconPhoto,
} from '@tabler/icons-react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete, confirmAction } from '@/lib/confirm'
import { useJobPostings } from '@/lib/swr'
import type { JobPosting, CareersContent, CareersBenefit } from '@/types'

export default function CareersPage() {
  return (
    <Stack>
      <Title order={2}>Хүний нөөц</Title>
      <Card withBorder>
        <Tabs defaultValue="jobs">
          <Tabs.List>
            <Tabs.Tab value="jobs" leftSection={<IconBriefcase size={14} />}>Ажлын байр</Tabs.Tab>
            <Tabs.Tab value="content" leftSection={<IconAdjustmentsAlt size={14} />}>Хуудасны агуулга</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="jobs" pt="md"><JobPostingsEditor /></Tabs.Panel>
          <Tabs.Panel value="content" pt="md"><CareersContentEditor /></Tabs.Panel>
        </Tabs>
      </Card>
    </Stack>
  )
}

// ─── Job Postings Editor ────────────────────────────────────────────────────

interface JobFormData {
  title: string
  description: string
  externalLink: string
  order: number
}

const emptyJob: JobFormData = { title: '', description: '', externalLink: '', order: 0 }

function JobPostingsEditor() {
  const { data, isLoading, mutate } = useJobPostings()
  const [items, setItems] = useState<JobPosting[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)

  useEffect(() => {
    const raw = data?.data as unknown
    const list: JobPosting[] = Array.isArray(raw)
      ? (raw as JobPosting[])
      : Array.isArray((raw as { data?: JobPosting[] })?.data)
        ? (raw as { data: JobPosting[] }).data
        : []
    setItems([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [data])

  const moveUp = (i: number) => {
    if (i === 0) return
    setItems((prev) => {
      const next = [...prev]
      ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
      return next
    })
    setOrderChanged(true)
  }
  const moveDown = (i: number) => {
    if (i === items.length - 1) return
    setItems((prev) => {
      const next = [...prev]
      ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
      return next
    })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу?', async () => {
    setReordering(true)
    try {
      await api.admin.reorderJobPostings(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false)
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    } finally {
      setReordering(false)
    }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)

  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<JobFormData>({
    defaultValues: emptyJob,
  })

  const openCreate = () => { setEditId(null); reset(emptyJob); setModalOpen(true) }
  const openEdit = (j: JobPosting) => {
    setEditId(j._id)
    reset({
      title: j.title,
      description: j.description || '',
      externalLink: j.externalLink || '',
      order: j.order,
    })
    setModalOpen(true)
  }
  const close = () => { setModalOpen(false); reset(emptyJob) }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload = { ...form, order: Number(form.order) || 0 }
      if (editId) await api.admin.updateJobPosting(editId, payload)
      else await api.admin.createJobPosting(payload)
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      close()
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const remove = (id: string) => confirmDelete('Энэ ажлын байрыг устгах уу?', async () => {
    try {
      await api.admin.deleteJobPosting(id)
      toast.success('Устгагдлаа')
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const toggle = async (j: JobPosting) => {
    try {
      await api.admin.updateJobPostingStatus(j._id, !j.isActive)
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={500}>Нээлттэй ажлын байрууд ({items.length})</Text>
        <Group gap="xs">
          {orderChanged && (
            <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>
              Дараалал хадгалах
            </Button>
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Нэмэх</Button>
        </Group>
      </Group>

      {orderChanged && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
          Дараалал өөрчлөгдсөн. "Дараалал хадгалах" товч дарж хадгална уу.
        </Alert>
      )}

      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={80}>Дараалал</Table.Th>
              <Table.Th>Гарчиг</Table.Th>
              <Table.Th>Тайлбар</Table.Th>
              <Table.Th>Холбоос</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th w={170}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((j, i) => (
              <Table.Tr key={j._id}>
                <Table.Td>
                  <Group gap={2} wrap="nowrap">
                    <Tooltip label="Дээш"><ActionIcon variant="subtle" color="gray" size="xs" disabled={i === 0} onClick={() => moveUp(i)}><IconArrowUp size={13} /></ActionIcon></Tooltip>
                    <Text size="xs" c="dimmed" w={16} ta="center">{i + 1}</Text>
                    <Tooltip label="Доош"><ActionIcon variant="subtle" color="gray" size="xs" disabled={i === items.length - 1} onClick={() => moveDown(i)}><IconArrowDown size={13} /></ActionIcon></Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td><Text size="sm" fw={600}>{j.title}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{j.description || '—'}</Text></Table.Td>
                <Table.Td>
                  {j.externalLink ? (
                    <a href={j.externalLink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <IconExternalLink size={12} />
                      <Text size="xs" style={{ maxWidth: 160, textOverflow: 'ellipsis', overflow: 'hidden' }} c="blue">Нээх</Text>
                    </a>
                  ) : <Text size="xs" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>
                  <Badge color={j.isActive ? 'green' : 'gray'} variant="light" size="sm">
                    {j.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon variant="light" size="sm" onClick={() => toggle(j)}>
                      {j.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                    </ActionIcon>
                    <ActionIcon variant="light" size="sm" onClick={() => openEdit(j)}>
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(j._id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={6}><Text ta="center" c="dimmed" py="lg">Ажлын байр бүртгэгдээгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </div>

      <Modal opened={modalOpen} onClose={close} title={editId ? 'Ажлын байр засах' : 'Шинэ ажлын байр'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="title"
              control={control}
              rules={{ validate: (v) => v.trim() ? true : 'Гарчиг шаардлагатай' }}
              render={({ field }) => <TextInput label="Гарчиг *" placeholder="Жнь: Анестезиолог эмч" {...field} error={errors.title?.message} />}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => <Textarea label="Товч тайлбар" placeholder="Тухайн ажлын байрны товч танилцуулга" autosize minRows={3} {...field} />}
            />
            <Controller
              name="externalLink"
              control={control}
              render={({ field }) => <TextInput label="External холбоос" placeholder="https://www.zangia.mn/job/..." leftSection={<IconExternalLink size={16} />} {...field} />}
            />
            <Group justify="flex-end">
              <Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}

// ─── Careers Content Editor (processImage + benefits[]) ─────────────────────

interface CareersFormData {
  processImage: string
  benefits: CareersBenefit[]
}

function CareersContentEditor() {
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const { control, handleSubmit, reset, setValue, watch, formState: { isSubmitting } } = useForm<CareersFormData>({
    defaultValues: { processImage: '', benefits: [] },
  })

  const { fields, append, remove, move } = useFieldArray({ control, name: 'benefits' })
  const watchImage = watch('processImage')

  const load = useCallback(() => {
    setLoading(true)
    api.admin.getWebsiteContent()
      .then((res: any) => {
        const c: CareersContent | undefined = res?.data?.careers ?? res?.careers
        reset({
          processImage: c?.processImage || '',
          benefits: c?.benefits || [],
        })
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Өгөгдөл ачаалахад алдаа гарлаа')
      })
      .finally(() => setLoading(false))
  }, [reset])

  useEffect(() => { load() }, [load])

  const handleImageUpload = async (file: File) => {
    setUploading(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue('processImage', result.url, { shouldDirty: true })
      toast.success('Зураг байршуулагдлаа')
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally {
      setUploading(false)
    }
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      await api.admin.updateCareersContent(form as unknown as Record<string, unknown>)
      toast.success('Хадгалагдлаа')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  return (
    <form onSubmit={onSubmit}>
      <Stack style={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} />

        {/* Process Banner */}
        <div>
          <Text size="sm" fw={600} mb={4}>Элсэх үйл явц — баннер зураг</Text>
          <Text size="xs" c="dimmed" mb={8}>Карьерын хуудсанд "Элсэх үйл явц" хэсэгт харагдах зураг.</Text>
          <Group>
            <Controller
              name="processImage"
              control={control}
              render={({ field }) => <TextInput style={{ flex: 1 }} placeholder="Зургийн URL" leftSection={<IconPhoto size={16} />} {...field} />}
            />
            <Button
              size="sm" variant="light" leftSection={<IconPhoto size={14} />}
              loading={uploading}
              onClick={() => {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = 'image/*'
                input.onchange = () => input.files?.[0] && handleImageUpload(input.files[0])
                input.click()
              }}
            >
              Upload
            </Button>
          </Group>
          {watchImage && (
            <img
              src={watchImage}
              alt="Элсэх үйл явц"
              style={{ marginTop: 12, maxWidth: '100%', maxHeight: 240, borderRadius: 12, objectFit: 'cover' }}
            />
          )}
        </div>

        {/* Benefits */}
        <div>
          <Group justify="space-between" mb={4}>
            <div>
              <Text size="sm" fw={600}>Яагаад НатурМед? — давуу талууд ({fields.length})</Text>
              <Text size="xs" c="dimmed">Карьер хуудсанд харагдах давуу тал тус бүр — icon нь SVG path data.</Text>
            </div>
            <Button
              size="xs" variant="light" leftSection={<IconPlus size={14} />}
              onClick={() => append({ title: '', description: '', icon: '' })}
            >
              Нэмэх
            </Button>
          </Group>
          <Stack gap="xs">
            {fields.map((f, i) => (
              <Card key={f.id} withBorder p="sm">
                <Group justify="space-between" mb="xs">
                  <Text size="xs" c="dimmed">#{i + 1}</Text>
                  <Group gap={4}>
                    <ActionIcon size="xs" variant="subtle" disabled={i === 0} onClick={() => move(i, i - 1)}>
                      <IconArrowUp size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" disabled={i === fields.length - 1} onClick={() => move(i, i + 1)}>
                      <IconArrowDown size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" color="red" variant="light" onClick={() => remove(i)}>
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Group>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Controller
                    name={`benefits.${i}.title` as const}
                    control={control}
                    render={({ field }) => <TextInput label="Нэр" placeholder="Жнь: Нийгмийн даатгал" size="xs" {...field} />}
                  />
                  <Controller
                    name={`benefits.${i}.description` as const}
                    control={control}
                    render={({ field }) => <TextInput label="Тайлбар" placeholder="Жнь: Бүрэн хамрагдана" size="xs" {...field} />}
                  />
                </SimpleGrid>
                <Controller
                  name={`benefits.${i}.icon` as const}
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      label="Icon (SVG path data)"
                      description='Жнь: M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z'
                      size="xs"
                      autosize
                      minRows={1}
                      mt="xs"
                      {...field}
                    />
                  )}
                />
              </Card>
            ))}
            {fields.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="md">Давуу тал нэмэгдээгүй байна</Text>
            )}
          </Stack>
        </div>

        <Group justify="flex-end">
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting} leftSection={<IconDeviceFloppy size={16} />}>
            Хадгалах
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
